import './ReturnTable.scss';

import React, { useState, useEffect, useMemo } from 'react';

import FormControl from 'react-bootstrap/FormControl';
import InputGroup from 'react-bootstrap/InputGroup';
import Button from 'react-bootstrap/Button';
import Image from 'react-bootstrap/Image';
import Table from 'react-bootstrap/Table';
import InfiniteScroll from 'react-infinite-scroll-component';

import { Spinner } from 'components/layout';

import axios from 'axios';
import moment from 'moment';
import { debounce } from 'lodash';

const table_header_names = {
  'return_number': 'Return Number',
  'status': 'Status',
  'subsidiary_name': 'Subsidiary',
  'customer_name': 'Customer Name',
  'created_by': 'Created By',
  'created_date': 'Created Date',
  'reason': 'Reason',
  'netsuite_number': 'NetSuite RTA Number',
  'rtn_status': 'NetSuite RTA Status'
}

/**
 * Custom Hook: useSortableData
 * @param data - the NCR data
 * @param config - ascending or descending, default null
 * @return {{requestSort: requestSort, sortConfig: unknown, sortedData: *[]}}
 */
const useSortableData = (data, config = null) => {

  const [sortConfig, setSortConfig] = useState(config);
  /**
   * This sorts the data and returns the sorted data
   * Then it's return to the calling component
   **/
  const orderedData = useMemo(() => {
    let sortableData = [...data];
    if (sortConfig !== null) {
      sortableData.sort((a, b) => {
        if (a[sortConfig.key] < b[sortConfig.key]) {
          return sortConfig.direction === 'ascending' ? -1 : 1;
        }
        if (a[sortConfig.key] > b[sortConfig.key]) {
          return sortConfig.direction === 'ascending' ? 1 : -1;
        }
        return 0;
      });
    }
    return sortableData;
  }, [data, sortConfig]);

  /**
   * FUNCTION: requestSort
   * This function will decide the sort option ascending vs descending
   * @param key - the table header we're interest in
   **/
  const requestSort = (key) => {
    let direction = 'ascending';
    if (sortConfig && sortConfig.key === key && sortConfig.direction === 'ascending') {
      direction = 'descending';
    }
    setSortConfig({ key, direction });
  };

  return { sortedData: orderedData, requestSort, sortConfig };
};

/***
 * This creates the NCR view component table
 * @return {JSX.Element}
 ***/
const ReturnTable = () => {

  const [data, setData] = useState([]);
  const [params, setParams] = useState({ take: 50 });
  const [hasMore, setHasMore] = useState(false);
  const [loading, setLoading] = useState(true);
  const { sortedData = data, requestSort, sortConfig } = useSortableData(data);

  const fetchData = (skip = 0) => {
    axios.get('/api/return-view', { params: { skip: skip, ...params } })
      .then(res => {
        setData(prev => {
          return skip
            ? [...prev, ...res.data]
            : [...res.data]
        });
        setHasMore(res.data.length === params.take);
        setLoading(false);
      });
  };

  useEffect(() => {
    fetchData();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [params]);

  const handleFilter = debounce((key, value) => {
    value = value.trimStart()
    if (key === 'return_number') {
      value = `${value}`.replace(/NCR0*/gi, '');
    };
    setParams(prev => {
      const updated = { ...prev };
      if (!value) {
        delete updated[key];
      } else {
        updated[key] = value;
      }
      return updated;
    });
  }, 1000);

  const getClassNamesFor = (name) => {
    if (!sortConfig) {
      return;
    }
    return sortConfig.key === name ? sortConfig.direction : undefined;
  };

  /***
   * FUNCTION: buildHeaders
   *    This function builders the table headers using the table_header_names
   *@return table header row with search filters.
   ***/
  const buildHeaders = () => {
    const headers = Object.entries(table_header_names).map(([key, value]) => {
      return (
        <th key={'header-key-' + key} className={'th-cell'}>
          <div className={'table-header ' + getClassNamesFor(key)} onClick={() => requestSort(key)}>{value}</div>
          <InputGroup>
            <FormControl onChange={({ target: { value } }) => handleFilter(key, value)} />
            <InputGroup.Append>
              <InputGroup.Text style={{ cursor: 'default' }}>
                <Image src={`${process.env.PUBLIC_URL}/images/filter.webp`} width='16' height='16' />
              </InputGroup.Text>
            </InputGroup.Append>
          </InputGroup>
        </th>
      )
    });
    const end = <th key={'end'} className={'th-cell'} />
    return [...headers, end]
  }

  /***
   * FUNCTION: buildRows
   * This function builds the rows for the application actively filtering the rows when an input is entered into
   * the search bar.
   * @return rows of NCR's in accordance to the filter options
   ***/
  const buildRows = () => {
    return sortedData.map(ncr => {
      return (
        <tr key={ncr.id} className={'table-row'}>
          <td>{ncr.return_number}</td>
          <td>{ncr.status}</td>
          <td>{ncr.subsidiary_name}</td>
          <td>{ncr.customer_name}</td>        
          <td>{ncr.created_by}</td>
          <td>{moment(ncr.created_date).format('DD/MM/YYYY')}</td>
          <td>{ncr.reason}</td>
          <td>{ncr.netsuite_number}</td>
          <td>{ncr.rtn_status}</td>
          <td className='text-center'>
            <Button variant='warning' className='view-button' href={`/return/${ncr.ncr_id}`}>View</Button>
          </td>
        </tr>
      )
    })
  }

  /**
   * @return the table containing the NCRs for this individual
   */
  return (
    <>
      {loading ? <Spinner size={150} /> :
        <div className='overflow-auto'>
          <InfiniteScroll
            dataLength={data.length}
            next={() => fetchData(data.length)}
            hasMore={hasMore}
            loader={<h4>Loading...</h4>}
          >
            <Table hover id='ncr-table' size='sm' className={'table-container'}>
              <thead>
                <tr className={'header-row'}>
                  {buildHeaders()}
                </tr>
              </thead>
              <tbody className={'table-body'}>
                {buildRows()}
              </tbody>
            </Table>
          </InfiniteScroll>
        </div>
      }
    </>
  )
}

export { ReturnTable };
